import React, { useState, useEffect } from 'react';
import {
  CalendarMonth,
  AccountBalanceWallet,
  ReceiptLong,
  AltRoute,
  Map as MapIcon,
  ChevronRight,
} from '@mui/icons-material';
import comms from './comms';
import Cash from './Dashboard/Cash';
import Future from './Dashboard/Future';
import TransactionsTabs from './Dashboard/TransactionsTabs';
import ClaimableRoutes from './Dispatch/ClaimableRoutes';

const BASE_TILES = [
  { id: 'future', label: 'Bookings', icon: CalendarMonth, component: Future },
  { id: 'transactions', label: 'Transactions', icon: ReceiptLong, component: TransactionsTabs },
  { id: 'cash', label: 'Cashiering', icon: AccountBalanceWallet, component: Cash },
];

// Route bidding tile is only shown when the fleet has the feature enabled.
const CLAIM_TILE = { id: 'claim', label: 'Claim Routes', icon: AltRoute, component: ClaimableRoutes };

function formatToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}/${m}/${day}`;
}

function Dashboard({ navigateTo, userSettings, fleetSettings }) {
  const [activeView, setActiveView] = useState('main');
  const [todayTrips, setTodayTrips] = useState(null); // null = loading, number = loaded, undefined = error
  const driverName = userSettings?.Name || userSettings?.name || '';

  // Route bidding tile appears only when the fleet config enables bidding.
  // The Route Bidding guide (§5) ships this as a boolean `routeBidding: true`;
  // accept that, plus the legacy `bidding: true` and `routeBidding.enabled`
  // object forms, so the tile shows regardless of which shape config sends.
  const biddingEnabled =
    fleetSettings?.bidding === true ||
    fleetSettings?.routeBidding === true ||
    fleetSettings?.routeBidding?.enabled === true;
  const tiles = biddingEnabled ? [...BASE_TILES, CLAIM_TILE] : BASE_TILES;

  useEffect(() => {
    let cancelled = false;
    const driverId = userSettings?.Id || JSON.parse(localStorage.getItem('userSettings') || '{}')?.Id;
    if (!driverId) {
      setTodayTrips(undefined);
      return;
    }
    comms
      .getDriverSchedule(driverId, formatToday())
      .then((res) => {
        if (cancelled) return;
        setTodayTrips(res?.status ? (res.trips || []).length : undefined);
      })
      .catch(() => !cancelled && setTodayTrips(undefined));
    return () => {
      cancelled = true;
    };
  }, [userSettings]);

  // ── Sub-page view ────────────────────────────────────────────────
  if (activeView !== 'main') {
    const tile = tiles.find((t) => t.id === activeView);
    if (tile) {
      const Component = tile.component;
      return (
        <div className="min-h-full bg-gray-100">
          <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 h-14 shadow-sm">
            <button
              onClick={() => setActiveView('main')}
              aria-label="Back"
              className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100"
            >
              <span className="text-2xl leading-none text-gray-800">&#8592;</span>
            </button>
            <h1 className="text-lg font-bold text-gray-900">{tile.label}</h1>
          </div>
          <div className="mx-auto max-w-md px-4 py-4">
            <Component navigateTo={navigateTo} userSettings={userSettings} fleetSettings={fleetSettings} />
          </div>
        </div>
      );
    }
  }

  // ── Home view ────────────────────────────────────────────────────
  return (
    <div className="min-h-full bg-gray-100">
      <div className="mx-auto max-w-md px-4 py-4 space-y-4">
        {/* Today summary card */}
        <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {driverName ? `Hi, ${driverName}` : "Today's bookings"}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {todayTrips === null ? (
                <span className="inline-block h-4 w-8 animate-pulse rounded bg-gray-200 align-middle" />
              ) : todayTrips === undefined ? (
                '—'
              ) : (
                `${todayTrips} ${todayTrips === 1 ? 'trip' : 'trips'} today`
              )}
            </span>
          </div>
        </div>

        {/* Tile grid */}
        <div className="grid grid-cols-2 gap-3">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                onClick={() => setActiveView(tile.id)}
                className="flex flex-col items-center gap-2 rounded-2xl bg-white px-4 py-6 shadow-sm transition-transform active:scale-95"
              >
                <Icon className="text-gray-700" style={{ fontSize: 30 }} />
                <span className="text-sm font-semibold text-gray-800">{tile.label}</span>
              </button>
            );
          })}

          {/* Dispatch entry */}
          <button
            onClick={() => navigateTo('dispatch')}
            className="col-span-2 flex items-center justify-between rounded-2xl bg-brand px-5 py-4 text-white shadow-sm transition-transform active:scale-95"
          >
            <span className="flex items-center gap-3">
              <MapIcon style={{ fontSize: 26 }} />
              <span className="text-base font-semibold">Dispatch</span>
            </span>
            <ChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
